import { useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  MentionMarkdown as SharedMentionMarkdown,
  agenticChatTransformLinkUri,
  type MentionMarkdownRenderSegmentArgs,
} from "@sudowrite/shared-chat";
import { cn } from "src/lib/cn";
import { createWebChatMarkdownLinkHandlers } from "../chatMarkdownLinkHandlers";
import { navigateToEntityMention } from "./navigateToEntityMention";

type Props = {
  text: string;
  className?: string;
  activeDocumentId?: string | null;
  activeProjectId?: string | null;
};

/**
 * Web adapter: shared mention segmentation/chips + react-markdown/GFM for text runs.
 */
export function MentionMarkdown({ text, className, activeDocumentId, activeProjectId }: Props) {
  const markdownOptions = useMemo(
    () => createWebChatMarkdownLinkHandlers(activeProjectId),
    [activeProjectId],
  );

  const renderMarkdownSegment = useCallback(
    ({ value, block, components }: MentionMarkdownRenderSegmentArgs) => (
      <ReactMarkdown
        className={cn(
          "inline text-left align-baseline",
          block && "block min-w-0 w-full",
          !block && "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
          "text-app-grey-0 text-base",
          "chat-markdown-content",
        )}
        remarkPlugins={[remarkGfm]}
        transformLinkUri={agenticChatTransformLinkUri}
        components={components as any}
      >
        {value}
      </ReactMarkdown>
    ),
    [],
  );

  return (
    <span className={cn("min-w-0 [&_a]:break-words", className)}>
      <SharedMentionMarkdown
        text={text}
        activeDocumentId={activeDocumentId}
        activeProjectId={activeProjectId}
        onMentionPress={(id) => {
          navigateToEntityMention(id, { activeDocumentId });
        }}
        renderMarkdownSegment={renderMarkdownSegment}
        markdownOptions={markdownOptions}
      />
    </span>
  );
}
